import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseRentalReportData } from '../base-rental/base-rental-report-data.model';
import { FiveHundredLookup } from '../car-repair-billing/five-hundred-lookup.model';
import { CarsInShop } from '../reporting/cars-in-shop.model';
import { ProgramWorkExport } from './program-work-export.model';
import { RevenueProjection} from './revenue-projection.model'
import { TemporarilyOffLease } from './Temporarily-Off-Lease.model';
import { StorageAccrual } from './storage-accrual.model';
import { IntacctResponseDetail } from './intacct-response-detail.model'
import { AccrualGeneralJournal, AccrualType } from '../../components/send-accrual-general-journal-to-intacct/accrual-general-journal.model'
import { RepairAccrualSummary } from './repair-accrual-summary.model'
import { SearchResult } from '../program-work/search-result.model';


@Injectable({providedIn: 'root'})
export class ReportingService {

	private url: string;

	constructor(private httpClient: HttpClient) {
		this.url = 'api/Report';
	}

	public getRepairAccrualReport(queryMonth: Date, format: string='PDF'): Observable<Blob> {
		let formatParm = undefined;
		let blobType = undefined;
		switch (format.toLowerCase())
		{
			case 'pdf':
				formatParm = '&format=pdf';
				blobType = 'application/pdf';
				break;
			case 'excel':
			case 'xls':	
				formatParm = '&format=excel';
				blobType = '';
				break;
			case 'csv':	
				formatParm = '&format=csv';
				blobType = '';
				break;
			default:
				formatParm = '&format=pdf';
				blobType = 'application/pdf';
				break;
		}

		return this.httpClient.get<Blob>(this.url + '/getRepairAccrualReport?queryMonth=' + queryMonth.toLocaleDateString() + formatParm,
			{ responseType: 'blob' as 'json' }).pipe(
			map(data => {
				const blob = new Blob([data], { type: blobType });
				return blob;
			}));
	}

	public getRepairAccrualReportPDF64(queryMonth: Date, format: string='PDF'): Observable<string> {

		return this.httpClient.get(this.url + '/getRepairAccrualReportPDF64?queryMonth=' + queryMonth.toLocaleDateString() + '&format=' + format, {responseType: 'text'});
	}

	public getCarRepairAccrualSummary(queryMonth: Date): Observable<RepairAccrualSummary[]> {

		return this.httpClient.get<RepairAccrualSummary[]>(this.url + '/getCarRepairAccrualSummary?queryMonth=' + queryMonth.toLocaleDateString());
	}

	public SendAccrualToIntacct(accrualGJ: AccrualGeneralJournal): Observable<any> {

		switch (accrualGJ.accrualType)
		{
			case AccrualType.CarRepairAccrual:
				return this.httpClient.post(this.url + '/SendCarRepairAccrualToIntacct?targetDate=' + accrualGJ.accrualTargeDate.toDateString(), accrualGJ)

			default:
				return this.httpClient.post(this.url + '/SendAccrualToIntacct', accrualGJ);
		}
	}

	public getWheelsReportData(processingPeriod: Date): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getWheelReportData?processingPeriod=' + processingPeriod.toLocaleString());
	}

	public getBaseRentalOwnershipData(processingPeriod: Date): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getBaseRentalOwnershipData?processingPeriod=' + processingPeriod.toLocaleDateString());
	}

	public getDaysWaitingCustomerApprovalData(start: Date, end: Date): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getDaysWaitingCustomerApprovalData?startDate=' + start.toLocaleString() + '&endDate=' + end.toLocaleString());
	}

	public getOwnerCrb500File(fiveHundredLookup: FiveHundredLookup ): Observable<Blob> {
		return this.httpClient.post(this.url + '/getBrc500BLine1File', fiveHundredLookup, { responseType: 'blob' }).pipe(
			map(data => {
				const blob = new Blob([data], { type: 'text/plain' });
				return blob;
			}));
	}

	public getCarsInShopReport(): Observable<Blob> {
		return this.httpClient.get<Blob>(this.url + '/GetCarsInShopReport', { responseType: 'blob' as 'json' }).pipe(
			map(data => {
				const blob = new Blob([data], { type: 'application/pdf' });
				return blob;
			}));
	}

	public getCarsInShopReportData(): Observable<CarsInShop[]> {
		return this.httpClient.get<CarsInShop[]>(this.url + '/GetCarsInShopReportData');
	}

	public getMonthlyCrdx2RailincCrbFile(glPostingDate: Date): Observable<any> {
		return this.httpClient.get(this.url + '/getMonthlyCrdx2RailincCrbFile?glPostingDate=' + glPostingDate.toLocaleDateString());
	}

	public getSoldCar500bReport(glPostingDate: Date, ownerCode: string, invoiceNUm: string): Observable<Blob> {
		return this.httpClient.get<Blob>(this.url + '/GetSoldCarBrc500BFile?glPostingDate=' + glPostingDate.toLocaleDateString() +'&ownerCode='+ ownerCode + '&invoiceNum='+invoiceNUm,
			{ responseType: 'blob' as 'json' }).pipe(
			map(data => {
				const blob = new Blob([data], { type: 'text/plain' });
				return blob;
			}));
	}

	public getProgramWorkReportData(): Observable<ProgramWorkExport[]> {
		return this.httpClient.get<ProgramWorkExport[]>(this.url + '/getProgramWorkReportData');
	}
	public getLeaseSummaryByCubeReport(): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getLeaseSummaryByCube')
	}

	public getRevenueProjection(startDate: Date, cfclOwner: boolean): Observable<RevenueProjection[]> {
		return this.httpClient.get<RevenueProjection[]>(this.url + '/getRevenueProjection?startDate=' + startDate.toLocaleDateString() + '&cfclOwned=' + cfclOwner);
	}

	public getBaseRentalAdjustmentReport(startDate: Date): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getBaseRentalAdjustmentReport?processingPeriod=' + startDate.toLocaleDateString());
	}

	public getLeaseListReport(): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getLeaseListReport');
	}
	public getLeaseOverrideContactReport(): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getLeaseOverrideReport');
	}
	public getBillingRentalAdjustmentReport(): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getBillingRentalAdjustmentReport')
	}

	public getBaseRentalOverlapRepot(): Observable<any>{
		return this.httpClient.get<any>(this.url + '/getBaseRentalOverlapReport');
	}

	public GetStorageAccural(): Observable<any[]>{
		return this.httpClient.get<any[]>(this.url + '/GetStorageAccural');
	}

	public getTemporarilyOffLeases(processingMonth: Date): Observable<TemporarilyOffLease[]> {
		return this.httpClient.get<TemporarilyOffLease[]>(this.url + '/getTemporarilyOffLeases?processingMonth=' + processingMonth.toLocaleDateString());
	}

	public sendToIntacct(storageAccural: StorageAccrual): Observable<IntacctResponseDetail[]> {
		return this.httpClient.post<IntacctResponseDetail[]>(this.url + '/sendToIntacct', storageAccural);
	}

	public getOwnershipStorageAccrual(month: string, year: number): Observable<StorageAccrual> {
		return this.httpClient.get<any>(this.url + '/getOwnershipStorageAccrual?month=' + month + '&year=' + year);
	}
	public getBaseRentalOverlapBasedOnBillingMonth(processingMonth: Date): Observable<any>{
		return this.httpClient.get<any>(this.url + '/getBaseRentalOverlapBasedOnBillingMonth?processingMonth=' + processingMonth.toLocaleDateString());
	}

	public getBaseRentalAudit(startDate: Date, endDate :Date): Observable<BaseRentalReportData[]>{
		return this.httpClient.get<BaseRentalReportData[]>(this.url + '/getBaseRentalAudit?startDate=' + startDate.toLocaleDateString() + '&endDate=' + endDate.toLocaleDateString());
	}

	public getAccrualJournalByReferenceNo(referenceNo: string): Observable<AccrualGeneralJournal>{
		return this.httpClient.get<AccrualGeneralJournal>(this.url + '/getAccrualGeneralJournalByReferenceNo?referenceNo=' + referenceNo);
	}

	public getCustomerRevenueReportData(startDate: Date, endDate :Date): Observable<BaseRentalReportData[]>{
		return this.httpClient.get<BaseRentalReportData[]>(this.url + '/getCustomerRevenueReportData?startDate=' + startDate.toLocaleDateString() + '&endDate=' + endDate.toLocaleDateString());
	}

	public getTaxComboReport(processedDate: Date): Observable<SearchResult>{
		return this.httpClient.get<SearchResult>(this.url + '/getTaxComboReport?processedDate=' + processedDate.toLocaleDateString());
	}
}
